import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Pet type configurations matching backend
const PET_CONFIGS = {
  dog: {
    name: "Dogs",
    singular: "Dog",
    icon: "dog",
    subdomain: "dogs",
    primaryColor: "#C2884A",
    secondaryColor: "#8B5A2B",
    parkName: "Dog Parks",
    breedName: "Breeds",
    communityName: "All American Dog Club",
    tagline: "Connect with fellow dog lovers"
  },
  cat: {
    name: "Cats",
    singular: "Cat",
    icon: "cat",
    subdomain: "cats",
    primaryColor: "#7C3AED",
    secondaryColor: "#5B21B6",
    parkName: "Cat Cafes & Spaces",
    breedName: "Breeds",
    communityName: "WhiskerPals",
    tagline: "Connect with fellow cat lovers"
  },
  bird: {
    name: "Birds",
    singular: "Bird",
    icon: "bird",
    subdomain: "birds",
    primaryColor: "#059669",
    secondaryColor: "#047857",
    parkName: "Aviaries & Bird Spaces",
    breedName: "Species",
    communityName: "FeatherPals",
    tagline: "Connect with fellow bird lovers"
  },
  fish: {
    name: "Fish",
    singular: "Fish",
    icon: "fish",
    subdomain: "fish",
    primaryColor: "#0EA5E9",
    secondaryColor: "#0284C7",
    parkName: "Aquariums & Shops",
    breedName: "Species",
    communityName: "FinPals",
    tagline: "Connect with fellow aquarists"
  },
  reptile: {
    name: "Reptiles",
    singular: "Reptile",
    icon: "turtle",
    subdomain: "reptiles",
    primaryColor: "#65A30D",
    secondaryColor: "#4D7C0F",
    parkName: "Reptile Expos & Shops",
    breedName: "Species",
    communityName: "ScalePals",
    tagline: "Connect with fellow reptile enthusiasts"
  },
  small_pet: {
    name: "Small Pets",
    singular: "Small Pet",
    icon: "rabbit",
    subdomain: "smallpets",
    primaryColor: "#EC4899",
    secondaryColor: "#DB2777",
    parkName: "Small Pet Meetups",
    breedName: "Breeds",
    communityName: "FluffyPals",
    tagline: "Connect with small pet lovers"
  },
  horse: {
    name: "Horses",
    singular: "Horse",
    icon: "horse",
    subdomain: "horses",
    primaryColor: "#92400E",
    secondaryColor: "#78350F",
    parkName: "Stables & Trails",
    breedName: "Breeds",
    communityName: "HoofPals",
    tagline: "Connect with fellow equestrians"
  }
};

const DEFAULT_PET_TYPE = 'dog';

const PetContext = createContext(null);

export const usePetContext = () => {
  const context = useContext(PetContext);
  if (!context) {
    throw new Error('usePetContext must be used within a PetContextProvider');
  }
  return context;
};

export const PetContextProvider = ({ children }) => {
  const [petType, setPetType] = useState(DEFAULT_PET_TYPE);
  const [config, setConfig] = useState(PET_CONFIGS[DEFAULT_PET_TYPE]);
  const [allPetTypes, setAllPetTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Detect pet type from subdomain
  useEffect(() => {
    const detectPetType = () => {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      
      if (parts.length >= 2) {
        const subdomain = parts[0].toLowerCase();
        // Find matching pet type
        for (const [type, cfg] of Object.entries(PET_CONFIGS)) {
          if (cfg.subdomain === subdomain) {
            return type;
          }
        }
      }
      
      // Check localStorage for manual selection
      const saved = localStorage.getItem('selectedPetType');
      if (saved && PET_CONFIGS[saved]) {
        return saved;
      }
      
      return DEFAULT_PET_TYPE;
    };

    const detected = detectPetType();
    setPetType(detected);
    setConfig(PET_CONFIGS[detected]);
    
    // Apply CSS variables for theming
    applyTheme(PET_CONFIGS[detected]);
    
    setLoading(false);
  }, []);

  // Fetch all pet types from backend
  useEffect(() => {
    const fetchPetTypes = async () => {
      try {
        const response = await axios.get(`${API}/api/pet-types`);
        setAllPetTypes(response.data);
      } catch (error) {
        console.error('Error fetching pet types:', error);
        // Use local config as fallback
        setAllPetTypes(Object.entries(PET_CONFIGS).map(([id, cfg]) => ({ id, ...cfg })));
      }
    };
    fetchPetTypes();
  }, []);

  // Apply theme colors as CSS variables
  // NOTE: Brand primary/secondary tokens are owned by ClubBrandingContext (which
  // writes --brand-primary / --brand-secondary / --brand-accent). Those variables
  // are mapped to shadcn's --primary / --secondary / --accent via index.css.
  // PetContext used to also stomp --primary/--secondary here, but that created
  // a double-write that overrode the club's brand tokens. This is now a no-op;
  // per-pet-type visual variation should ride on club branding + status colors.
  const applyTheme = (_cfg) => {
    // Intentionally left blank — see note above.
  };

  // Switch pet type (for pet switcher UI)
  const switchPetType = (newType) => {
    if (PET_CONFIGS[newType]) {
      setPetType(newType);
      setConfig(PET_CONFIGS[newType]);
      localStorage.setItem('selectedPetType', newType);
      applyTheme(PET_CONFIGS[newType]);
      
      // Set header for API calls
      axios.defaults.headers.common['X-Pet-Type'] = newType;
    }
  };

  // Get API headers with pet type
  const getApiHeaders = () => ({
    'X-Pet-Type': petType
  });

  const value = {
    petType,
    config,
    allPetTypes,
    loading,
    switchPetType,
    getApiHeaders,
    PET_CONFIGS,
    // Convenience getters
    petName: config.name,
    petSingular: config.singular,
    communityName: config.communityName,
    parkName: config.parkName,
    breedName: config.breedName,
    tagline: config.tagline,
    primaryColor: config.primaryColor,
    secondaryColor: config.secondaryColor
  };

  return (
    <PetContext.Provider value={value}>
      {children}
    </PetContext.Provider>
  );
};

export default PetContext;
